import { type ReactElement, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useStoreProductDetailQuery } from '@/entities/product';
import { type SizeFieldValues, sizeTotalsFromFields } from '@/entities/quote';
import { useRecordProductView } from '@/features/product-view-recording';
import { STORE_DETAIL_DESCRIPTION_ID, STORE_PATH } from '@/shared/config';
import { Button, buttonVariants, PageHeading, SitePageShell } from '@/shared/ui';
import { PriceTierTable } from '@/widgets/price-tier-table';
import { ProductImageGallery } from '@/widgets/product-image-gallery';
import { ProductInfoPanel } from '@/widgets/product-info-panel';
import { ProductReviewSection } from '@/widgets/product-review-section';
import { ProductionProcess } from '@/widgets/production-process';
import { SiteFooter } from '@/widgets/site-footer';
import { SiteHeader } from '@/widgets/site-header';

/** 미공개(NOT_FOUND)·잘못된 주소(BAD_REQUEST)는 같은 404 화면 — 존재를 구분해 주지 않는다. */
const isNotFoundLike = (code: unknown): boolean => code === 'NOT_FOUND' || code === 'BAD_REQUEST';

/** 스토어 상품 상세(/store/:productId) — 확인 후 커스터마이징으로 전환하는 화면. */
export const StoreDetailPage = (): ReactElement => {
  const { productId = '' } = useParams();
  const detailQuery = useStoreProductDetailQuery(productId);
  // 회원 조회 기록(SUPER-82) — 상세가 정상으로 열렸을 때만. 404·오류 화면에서는 남기지 않는다.
  useRecordProductView(productId, detailQuery.isSuccess);
  const [selectedColorId, setSelectedColorId] = useState<string | null>(null);
  // 수량 입력 — 우측 수량 선택과 아래 수량별 가격 표의 강조가 같은 값을 본다. null = 아직 손대지 않음.
  const [qtyDraftState, setQtyDraftState] = useState<string | null>(null);
  // 사이즈 상품의 사이즈별 수량 — 합계가 수량이다.
  const [sizeDrafts, setSizeDrafts] = useState<SizeFieldValues>({});

  if (detailQuery.isPending) {
    return (
      <SitePageShell header={<SiteHeader />} footer={<SiteFooter />}>
        <p role="status" className="sr-only">
          상품 정보를 불러오는 중입니다.
        </p>
        <div
          aria-hidden
          className="grid gap-12 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] xl:gap-16"
        >
          <div className="mx-auto aspect-square w-full max-w-xl bg-muted/70 lg:max-w-none" />
          <div className="min-h-96 bg-muted/70" />
        </div>
      </SitePageShell>
    );
  }

  if (detailQuery.isError) {
    const code = detailQuery.error.data?.code;

    return (
      <SitePageShell header={<SiteHeader />} footer={<SiteFooter />}>
        <section className="flex flex-col items-center gap-4 py-24 text-center">
          {isNotFoundLike(code) ? (
            <PageHeading
              className="items-center text-center"
              title="상품을 찾을 수 없습니다"
              description="판매가 종료됐거나 잘못된 주소일 수 있습니다."
            />
          ) : (
            // 자동 재시도(retry)가 꺼져 있어 일시 오류는 사용자가 직접 재조회한다 — 목록·트리 오류와 같은 패턴.
            // 원문 오류는 화면에 싣지 않는다.
            <div role="alert" className="flex flex-col items-center gap-4">
              <p className="text-sm text-destructive">상품 정보를 불러오지 못했습니다.</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void detailQuery.refetch();
                }}
              >
                다시 시도
              </Button>
            </div>
          )}
          <Link to={STORE_PATH} className={buttonVariants({ variant: 'outline', size: 'lg' })}>
            상품 목록으로
          </Link>
        </section>
      </SitePageShell>
    );
  }

  const detail = detailQuery.data;
  // 기본 선택 = 첫 수급 가능 색상(와이어프레임). 단종만 있으면 선택 없음.
  const activeColorId =
    selectedColorId ?? detail.colorOptions.find((option) => option.isAvailable)?.id ?? null;
  const activeColor = detail.colorOptions.find((option) => option.id === activeColorId) ?? null;
  // 기본 수량 = 최소 주문 수량 — 첫 구간이 강조되고 합계도 그 기준으로 시작한다.
  // 사이즈 상품은 사이즈별 수량의 합계(형식이 틀린 칸이 있으면 빈 값)다.
  const sizeTotals =
    detail.sizeOptions.length > 0 ? sizeTotalsFromFields(detail.sizeOptions, sizeDrafts) : null;
  const qtyDraft =
    detail.sizeOptions.length > 0
      ? sizeTotals === null
        ? ''
        : String(sizeTotals.total)
      : (qtyDraftState ?? String(detail.minOrderQty));

  return (
    <SitePageShell header={<SiteHeader />} footer={<SiteFooter />}>
      <div className="grid gap-12 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] xl:gap-16">
        <ProductImageGallery
          key={activeColorId ?? 'base'}
          productName={detail.name}
          images={detail.images}
          previewImage={activeColor?.previewImage ?? null}
        />

        <ProductInfoPanel
          detail={detail}
          selectedColorId={activeColorId}
          onSelectColor={setSelectedColorId}
          qtyDraft={qtyDraft}
          onChangeQtyDraft={setQtyDraftState}
          sizeDrafts={sizeDrafts}
          onChangeSizeDraft={(sizeOptionId, value) =>
            setSizeDrafts((previous) => ({ ...previous, [sizeOptionId]: value }))
          }
        />
      </div>

      {detail.priceTiers.length > 0 && (
        <div className="mt-24">
          <PriceTierTable tiers={detail.priceTiers} qtyDraft={qtyDraft} />
        </div>
      )}

      {detail.detailImage !== null && (
        // 위 바로가기 탭이 이 섹션으로 내려온다 — 붙어 있는 헤더에 제목이 가리지 않게 띄운다.
        <section aria-labelledby={STORE_DETAIL_DESCRIPTION_ID} className="mt-24 scroll-mt-28">
          <h2
            id={STORE_DETAIL_DESCRIPTION_ID}
            className="border-b border-foreground pb-4 text-2xl leading-tight font-bold tracking-[-0.02em]"
          >
            상세 설명
          </h2>
          <div className="mx-auto mt-8 max-w-4xl overflow-hidden bg-muted/70">
            <img src={detail.detailImage.url} alt={`${detail.name} 상세 설명`} className="w-full" />
          </div>
        </section>
      )}

      {/* 상세 설명(사진) 끝·후기 앞 — 클라이언트 요청 위치. 상세 이미지가 없어도 보인다. */}
      <ProductionProcess />

      <ProductReviewSection productId={detail.id} />
    </SitePageShell>
  );
};
